import Typography from "components/typography";
import React, { useState } from "react";
import styled from "styled-components";
import { Colors } from "utils/colors";
import TrackMenu from "../track-menu";
import { IDiscographyType } from "types/discographyType";
import { Link } from "react-router-dom";
import { ITrackRelease } from "types/musicData";
import DateConvert from "utils/dateConvert";
import { SongWithUrls } from "types/response-type";
import { ReleaseContainerWithUrls } from "types/response-type";
import Modal from "components/modal";
import TakedownTrackModal from "components/modal/modals/takedown-track-modal";

interface IProps {
  data: SongWithUrls | ReleaseContainerWithUrls;
}

const isSongWithUrls = (
  data: SongWithUrls | ReleaseContainerWithUrls
): data is SongWithUrls => {
  return "releaseType" in data;
};
const TrackBlockItem = ({ data }: IProps) => {
  const [openMenu, setOpenMenu] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isSong = isSongWithUrls(data);

  // Get common properties based on type
  const {
    title,
    coverArtPath,
    status,
    releaseType,
    proposedReleaseDate,
    primaryArtist
  } = isSong
    ? data
    : {
        title: data.title,
        coverArtPath: data.coverArtPath,
        status: data.status,
        releaseType: data.type,
        proposedReleaseDate: data.releaseDate,
        primaryArtist: {
          name: data.tracks[0]?.primaryArtist?.name || "Unknown Artist"
        }
      };

  const TakedownTrack = () => {};

  return (
    <>
      <ItemContainer>
        <Status status={data.status}>
          <Typography color={Colors.white300}>{data.status}</Typography>
        </Status>
        <Image src={data.coverArtPath} alt={data.title} />
        <TextArea>
          <div>
            <FlexBox>
              <Typography size="lg" weight="bold" mr="0.8">
                {data.title}
              </Typography>
              <Typography color={Colors.grey500} size="s" lh="1.6">
                {releaseType} , {DateConvert(new Date(proposedReleaseDate))}
              </Typography>
            </FlexBox>
            <TextLink to={"/"}>
              <Typography
                color={Colors.yellow300}
                decoration="underline"
                mb="0.7rem"
              >
                {primaryArtist.name}
              </Typography>
            </TextLink>
          </div>
          <OptionsButton onClick={() => setOpenMenu(true)}>
            <TrackMenu
              isopen={openMenu}
              closeMenu={(value) => setOpenMenu(false)}
              trackId={data.id}
              type={isSong ? releaseType : data.type}
              takedown={() => setIsModalOpen(true)}
            />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="100%"
              height="100%"
              viewBox="0 0 30 30"
              fill="none"
            >
              <path
                d="M15.0469 8.75049C13.6661 8.75049 12.5469 7.6312 12.5469 6.25049C12.5469 4.86978 13.6661 3.75049 15.0469 3.75049C16.4276 3.75049 17.5469 4.86978 17.5469 6.25049C17.5469 7.6312 16.4276 8.75049 15.0469 8.75049Z"
                fill="#CDCDCD"
              />
              <path
                d="M15 17.5C13.6193 17.5 12.5 16.3807 12.5 15C12.5 13.6193 13.6193 12.5 15 12.5C16.3807 12.5 17.5 13.6193 17.5 15C17.5 16.3807 16.3807 17.5 15 17.5Z"
                fill="#CDCDCD"
              />
              <path
                d="M14.9512 26.25C13.5704 26.25 12.4512 25.1307 12.4512 23.75C12.4512 22.3693 13.5704 21.25 14.9512 21.25C16.3319 21.25 17.4512 22.3693 17.4512 23.75C17.4512 25.1307 16.3319 26.25 14.9512 26.25Z"
                fill="#CDCDCD"
              />
            </svg>
          </OptionsButton>
        </TextArea>
      </ItemContainer>
      <Modal isActive={isModalOpen} closeModal={() => setIsModalOpen(false)}>
        <TakedownTrackModal
          closeModal={() => setIsModalOpen(false)}
          confirm={TakedownTrack}
          name={title}
        />
      </Modal>
    </>
  );
};

export default TrackBlockItem;

const ItemContainer = styled.div`
  padding: 0.8rem;
  height: 100%;
  width: 100%;
  border-radius: 0.8rem;
  background: ${Colors.grey700};
  position: relative;
  overflow: hidden;
`;

const Image = styled.img`
  width: 100%;
  object-fit: cover;
  border-radius: 0.8rem;
  margin-bottom: 2.1rem;
`;

const Status = styled.div<{ status: string }>`
  width: fit-content;
  position: absolute;
  padding: 0.4rem 0.8rem;
  top: 0;
  left: 0;
  border-radius: 0px 4px 4px 0px;
  box-shadow: 0px 4px 5.5px 0px rgba(0, 0, 0, 0.15);
  background-color: ${(props) =>
    props.status === "released"
      ? Colors.success
      : props.status === "inactive"
      ? Colors.danger
      : Colors.yellow500};
`;

const FlexBox = styled.div`
  display: flex;
  align-items: end;
  margin-bottom: 0.4rem;
`;
const TextArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const TextLink = styled(Link)`
  all: unset;
  cursor: pointer;
`;

const OptionsButton = styled.div`
  width: 3rem;
  height: 3rem;
  cursor: pointer;
  position: relative;
`;
